import React, { useEffect, useRef, useState } from "react";
import { useShortcut } from "../shortcuts/useShortcut.js";
import { comboHint } from "../shortcuts/registry.js";
import { useIncrementalList } from "../hooks/useIncrementalList.js";
import { ListSentinel } from "./ListSentinel.jsx";

/**
 * Desktop list + detail split (saas-books SplitPane): the list keeps ~37% on the left,
 * the open record fills the right. Drag the divider to resize (ratio remembered),
 * double-click it to reset, or focus it and use ←/→.
 */
const DEFAULT_RATIO = 0.37;
const MIN_LEFT = 280;
const MIN_RIGHT = 420;

function readRatio(key) {
  const value = Number(localStorage.getItem(key));
  return value > 0.15 && value < 0.85 ? value : DEFAULT_RATIO;
}

export function SplitPane({ storageKey, left, right }) {
  const ratioKey = `books_split_${storageKey}`;
  const [ratio, setRatio] = useState(() => readRatio(ratioKey));
  const containerRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(ratioKey, String(ratio));
  }, [ratioKey, ratio]);

  const clampRatio = (next) => {
    const width = containerRef.current?.getBoundingClientRect().width || 1;
    const min = MIN_LEFT / width;
    const max = 1 - MIN_RIGHT / width;
    return Math.min(Math.max(next, min), Math.max(min, max));
  };

  const startDrag = (e) => {
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const onMove = (ev) => setRatio(clampRatio((ev.clientX - rect.left) / rect.width));
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.classList.remove("col-resizing");
    };
    document.body.classList.add("col-resizing");
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") setRatio((r) => clampRatio(r - 0.03));
    else if (e.key === "ArrowRight") setRatio((r) => clampRatio(r + 0.03));
    else return;
    e.preventDefault();
  };

  return (
    <div className="split-pane" ref={containerRef}>
      <div className="split-pane-left" style={{ flexBasis: `${ratio * 100}%` }}>{left}</div>
      <div
        className="split-pane-divider"
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        tabIndex={0}
        title="Drag to resize · double-click to reset"
        onMouseDown={startDrag}
        onDoubleClick={() => setRatio(DEFAULT_RATIO)}
        onKeyDown={handleKeyDown}
      />
      <div className="split-pane-right">{right}</div>
    </div>
  );
}

/**
 * Compact record list for the split view's left pane (search + rows).
 * renderRow(row) returns { title, subtitle, amount, badge }.
 */
export function RecordList({ rows, selectedId, onSelect, renderRow, searchValue, onSearch, searchPlaceholder, noun, resetKey }) {
  // Render far enough to include the open record, even deep in the list
  const selectedIndex = rows.findIndex((row) => row.id === selectedId);
  const pageSize = Math.max(30, Math.ceil((selectedIndex + 1) / 30) * 30);
  const list = useIncrementalList(rows, { resetKey, pageSize });
  const selectedRef = useRef(null);

  // Keep the open record visible when it changes via [ / ]
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedId]);

  return (
    <div className="record-list">
      <div className="search-container record-list-search">
        <span className="search-icon">🔍</span>
        <input type="text" className="search-input" placeholder={searchPlaceholder} value={searchValue} onChange={(e) => onSearch(e.target.value)} />
      </div>
      <div className="record-list-rows" role="listbox" aria-label={noun}>
        {list.visibleItems.map((row) => {
          const { title, subtitle, amount, badge } = renderRow(row);
          const isSelected = row.id === selectedId;
          return (
            <button
              key={row.id}
              ref={isSelected ? selectedRef : undefined}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={`record-list-row ${isSelected ? "selected" : ""}`}
              onClick={() => onSelect(row)}
            >
              <span className="record-list-main">
                <span className="record-list-title">{title}</span>
                <span className="record-list-sub">{subtitle}</span>
              </span>
              <span className="record-list-side">
                <span className="record-list-amount">{amount}</span>
                {badge}
              </span>
            </button>
          );
        })}
        <ListSentinel sentinelRef={list.sentinelRef} hasMore={list.hasMore} visibleCount={list.visibleCount} total={list.total} onShowMore={list.showMore} noun={noun} />
      </div>
    </div>
  );
}

/** "◀ 3 of 92 ▶" record stepper for the detail pane; binds [ / ] / Esc. */
export function RecordNav({ index, total, onPrev, onNext, onClose }) {
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < total - 1;
  useShortcut("record_prev", onPrev, { disabled: !hasPrev });
  useShortcut("record_next", onNext, { disabled: !hasNext });
  useShortcut("record_close", onClose);

  return (
    <div className="record-nav">
      <button type="button" className="btn-icon" onClick={onPrev} disabled={!hasPrev} title={`Previous record (${comboHint("record_prev")})`} aria-label="Previous record">
        ◀
      </button>
      <span className="record-nav-count">{index >= 0 ? `${index + 1} of ${total}` : `— of ${total}`}</span>
      <button type="button" className="btn-icon" onClick={onNext} disabled={!hasNext} title={`Next record (${comboHint("record_next")})`} aria-label="Next record">
        ▶
      </button>
    </div>
  );
}

/** Scrolls to the top while the split view is shown and restores the list's scroll position after. */
export function useSplitScroll(active) {
  const listY = useRef(0);

  // Track the list's position continuously: once the split renders, the shorter page already clamps scrollY
  useEffect(() => {
    const onScroll = () => {
      if (!document.querySelector(".split-pane")) listY.current = window.scrollY;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    const savedY = listY.current;
    window.scrollTo(0, 0);
    return () => {
      requestAnimationFrame(() => window.scrollTo(0, savedY));
    };
  }, [active]);
}
